<template>
  <BaseSheet :isOpen="isOpen" size="sm" @close="handleClose">
    <!-- Idle / Sending -->
    <template v-if="phase === 'idle' || phase === 'sending'">
      <div class="flex flex-col items-center text-center">
        <!-- Icon -->
        <div class="mb-4">
          <div class="w-14 h-14 rounded-full flex items-center justify-center qrc-icon-ring">
            <ComputerDesktopIcon class="h-7 w-7" style="color: var(--apex-cyan);" />
          </div>
        </div>

        <!-- Title & subtitle -->
        <h3 class="text-[17px] font-bold text-foreground tracking-tight mb-1">
          {{ t('qrlogin_confirm.title') }}
        </h3>
        <p class="text-[13px] leading-relaxed text-muted-foreground mb-5">
          {{ t('qrlogin_confirm.subtitle') }}
        </p>

        <!-- Verification code -->
        <div class="flex flex-col items-center gap-1.5 mb-2">
          <span class="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('qrlogin_confirm.verification_label') }}
          </span>
          <span class="qrc-verification-badge px-3 py-1.5 rounded-lg font-mono text-[18px] font-bold tracking-[0.18em]">
            {{ verificationCode }}
          </span>
        </div>
        <p class="text-[12px] text-muted-foreground mb-6 max-w-[260px]">
          {{ t('qrlogin_confirm.verification_hint') }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-3">
        <button type="button" @click="handleClose"
          class="flex-1 h-[48px] rounded-[14px] text-[14px] font-semibold qrc-cancel active:scale-[0.97] transition-all duration-150">
          {{ t('qrlogin_confirm.cancel') }}
        </button>
        <button type="button" @click="approveQrLogin" :disabled="phase === 'sending'"
          class="flex-1 h-[48px] rounded-[14px] text-[14px] font-bold text-white flex items-center justify-center gap-2 active:scale-[0.97] transition-all duration-150 disabled:opacity-70 disabled:active:scale-100 qrc-cta">
          <svg v-if="phase === 'sending'" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
            fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span v-if="phase !== 'sending'">{{ t('qrlogin_confirm.approve') }}</span>
        </button>
      </div>
    </template>

    <!-- Success -->
    <template v-else-if="phase === 'success'">
      <div class="flex flex-col items-center text-center py-2">
        <div class="relative mb-4">
          <div class="absolute -inset-3 rounded-full animate-pulse"
            style="background: radial-gradient(circle, color-mix(in srgb, #22C55E 15%, transparent) 0%, transparent 65%);">
          </div>
          <div class="w-14 h-14 rounded-full flex items-center justify-center"
            style="background: linear-gradient(145deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.05)); border: 1.5px solid rgba(34, 197, 94, 0.2);">
            <CheckCircleIcon class="h-7 w-7 text-green-500" />
          </div>
        </div>
        <h3 class="text-[17px] font-bold text-foreground tracking-tight">
          {{ t('qrlogin_confirm.success') }}
        </h3>
      </div>
    </template>

    <!-- Error -->
    <template v-else-if="phase === 'error'">
      <div class="flex flex-col items-center text-center">
        <div class="mb-4">
          <div class="w-14 h-14 rounded-full flex items-center justify-center"
            style="background: linear-gradient(145deg, rgba(239, 68, 68, 0.13), rgba(239, 68, 68, 0.04)); border: 1.5px solid rgba(239, 68, 68, 0.16);">
            <ExclamationTriangleIcon class="h-7 w-7 text-red-500" />
          </div>
        </div>
        <p class="text-[13px] text-muted-foreground mb-6 max-w-[260px]">
          {{ errorKey ? t(errorKey) : '' }}
        </p>
      </div>

      <div class="flex items-center gap-3">
        <button type="button" @click="handleClose"
          class="flex-1 h-[48px] rounded-[14px] text-[14px] font-semibold qrc-cancel active:scale-[0.97] transition-all duration-150">
          {{ t('qrlogin_confirm.cancel') }}
        </button>
        <button type="button" @click="approveQrLogin"
          class="flex-1 h-[48px] rounded-[14px] text-[14px] font-bold text-white flex items-center justify-center gap-2 active:scale-[0.97] transition-all duration-150 qrc-cta">
          {{ t('qrlogin_confirm.approve') }}
        </button>
      </div>
    </template>
  </BaseSheet>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ComputerDesktopIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import BaseSheet from './BaseSheet.vue'
import { useQrLoginConfirm } from '../../composables/useQrLoginConfirm'
import { useSnackbarStore } from '../../stores/snackbarStore'

const { t } = useI18n()
const snackbarStore = useSnackbarStore()

const {
  isOpen,
  phase,
  errorKey,
  pendingRejection,
  verificationCode,
  approveQrLogin,
  closeQrLoginConfirm,
} = useQrLoginConfirm()

const handleClose = () => {
  if (phase.value === 'sending' || phase.value === 'success') return
  closeQrLoginConfirm()
}

// Show translated snackbar when the composable rejects a deep link (authority mismatch, not authenticated)
watch(pendingRejection, (key) => {
  if (key) {
    snackbarStore.addSnackbar(t(key), 'error')
    pendingRejection.value = null
  }
})

// Auto-close on success after delay, then show snackbar
watch(phase, (newPhase) => {
  if (newPhase === 'success') {
    setTimeout(() => {
      closeQrLoginConfirm()
      snackbarStore.addSnackbar(t('qrlogin_confirm.success'), 'success')
    }, 1500)
  }
})
</script>

<style scoped>
/* Icon ring */
.qrc-icon-ring {
  background: linear-gradient(145deg,
    color-mix(in srgb, var(--apex-cyan) 13%, transparent),
    color-mix(in srgb, var(--apex-cyan) 4%, transparent));
  border: 1.5px solid color-mix(in srgb, var(--apex-cyan) 16%, transparent);
}

/* Verification badge */
.qrc-verification-badge {
  color: var(--apex-cyan);
  background: color-mix(in srgb, var(--apex-cyan) 8%, transparent);
  border: 1.5px solid color-mix(in srgb, var(--apex-cyan) 18%, transparent);
}

/* Cancel button — light */
.qrc-cancel {
  color: var(--muted-foreground, #64748b);
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  cursor: pointer;
}
.qrc-cancel:active {
  background: rgba(0, 0, 0, 0.07);
}

/* CTA button */
.qrc-cta {
  background: linear-gradient(135deg, #06C8FF 0%, #009FE3 100%);
  box-shadow: 0 6px 24px rgba(6, 200, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  cursor: pointer;
}

/* Dark mode */
:is(.dark *).qrc-cancel {
  color: var(--muted-foreground, #94a3b8);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.07);
}
:is(.dark *).qrc-cancel:active {
  background: rgba(255, 255, 255, 0.10);
}
:is(.dark *).qrc-cta {
  box-shadow: 0 4px 20px rgba(6, 200, 255, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.10);
}
</style>
